import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const fmtDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("sr-RS");
  } catch {
    return value;
  }
};

const fmtAmount = (value) => {
  const n = Number(value || 0);
  return new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};




export default function BankPage() {
  const [activeTab, setActiveTab] = useState("statements");

  const [statements, setStatements] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [loadingStatements, setLoadingStatements] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  const [statementError, setStatementError] = useState("");
  const [transactionError, setTransactionError] = useState("");

  const [statementFilters, setStatementFilters] = useState({
    accountNumber: "",
    dateFrom: "",
    dateTo: "",
  });

  const [transactionFilters, setTransactionFilters] = useState({
    accountNumber: "",
    dateFrom: "",
    dateTo: "",
    direction: "",
    bookingCategory: "",
    search: "",
  });
  
  const [editingTxId, setEditingTxId] = useState(null);
const [classificationForm, setClassificationForm] = useState({
  bankTransactionId: null,
  projectId: "",
  category: "",
  subcategory: "",
  note: "",
});


  const startClassificationEdit = (row) => {
  setEditingTxId(row.id);
  setClassificationForm({
    bankTransactionId: row.id,
    projectId: row.project_id || "",
    category: row.category || "",
    subcategory: row.subcategory || "",
    note: row.note || "",
  });
};

const cancelClassificationEdit = () => {
  setEditingTxId(null);
  setClassificationForm({
    bankTransactionId: null,
    projectId: "",
    category: "",
    subcategory: "",
    note: "",
  });
};

const formatYAxisTick = (value) => {
  const abs = Math.abs(value);

  if (abs >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(".0", "")}M`;
  }

  if (abs >= 1_000) {
    return `${(value / 1_000).toFixed(0)}k`;
  }

  return value?.toString() ?? "";
};

const formatDateTick = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("sr-RS", {
    day: "numeric",
    month: "numeric"
  });
};

const formatTooltipLabel = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("sr-RS", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};
const [suppliers, setSuppliers] = useState([]);
const [customers, setCustomers] = useState([]);
const [projectsSummary, setProjectsSummary] = useState([]);

const [loadingSuppliers, setLoadingSuppliers] = useState(false);
const [loadingCustomers, setLoadingCustomers] = useState(false);
const [loadingProjectsSummary, setLoadingProjectsSummary] = useState(false);

const [suppliersError, setSuppliersError] = useState("");
const [customersError, setCustomersError] = useState("");
const [projectsSummaryError, setProjectsSummaryError] = useState("");

const [documentsByTx, setDocumentsByTx] = useState({});
const [documentsModalTx, setDocumentsModalTx] = useState(null);
const [documentsModalItems, setDocumentsModalItems] = useState([]);

const saveClassification = async () => {
  try {
    await axios.post(
      "https://modularobjects.com/mailserver/api/bank/classification",
      {
        bankTransactionId: classificationForm.bankTransactionId,
        projectId: classificationForm.projectId || null,
        category: classificationForm.category || null,
        subcategory: classificationForm.subcategory || null,
        note: classificationForm.note || null,
        classifiedBy: "manual-ui",
      }
    );

    await loadTransactions();
    cancelClassificationEdit();
  } catch (err) {
    console.error("Greška pri snimanju klasifikacije:", err);
    alert("Neuspešno snimanje klasifikacije.");
  }
};

  const loadStatements = async () => {
    setLoadingStatements(true);
    setStatementError("");

    try {
      const params = {};
      if (statementFilters.accountNumber) params.accountNumber = statementFilters.accountNumber;
      if (statementFilters.dateFrom) params.dateFrom = statementFilters.dateFrom;
      if (statementFilters.dateTo) params.dateTo = statementFilters.dateTo;
      params.limit = 200;

      const res = await axios.get("https://modularobjects.com/mailserver/api/bank/statements", { params });
      setStatements(res.data?.data || []);
    } catch (err) {
      console.error("Greška pri učitavanju izvoda:", err);
      setStatementError("Neuspešno učitavanje izvoda.");
    } finally {
      setLoadingStatements(false);
    }
  };

  const loadTransactions = async () => {
    setLoadingTransactions(true);
    setTransactionError("");

    try {
      const params = {};
      if (transactionFilters.accountNumber) params.accountNumber = transactionFilters.accountNumber;
      if (transactionFilters.dateFrom) params.dateFrom = transactionFilters.dateFrom;
      if (transactionFilters.dateTo) params.dateTo = transactionFilters.dateTo;
      if (transactionFilters.direction) params.direction = transactionFilters.direction;
      if (transactionFilters.bookingCategory) params.bookingCategory = transactionFilters.bookingCategory;
      if (transactionFilters.search) params.search = transactionFilters.search;
      params.limit = 500;

      const res = await axios.get("https://modularobjects.com/mailserver/api/bank/transactions", { params });
      setTransactions(res.data?.data || []);
    } catch (err) {
      console.error("Greška pri učitavanju transakcija:", err);
      setTransactionError("Neuspešno učitavanje transakcija.");
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadSuppliers = async () => {
  setLoadingSuppliers(true);
  setSuppliersError("");

  try {
    const params = {};
    if (transactionFilters.dateFrom) params.dateFrom = transactionFilters.dateFrom;
    if (transactionFilters.dateTo) params.dateTo = transactionFilters.dateTo;
    if (transactionFilters.search) params.search = transactionFilters.search;

    const res = await axios.get(
      "https://modularobjects.com/mailserver/api/bank/suppliers-summary",
      { params }
    );

    setSuppliers(res.data?.data || []);
  } catch (err) {
    console.error("Greška pri učitavanju dobavljača:", err);
    setSuppliersError("Neuspešno učitavanje dobavljača.");
  } finally {
    setLoadingSuppliers(false);
  }
};

async function uploadDoc(id, file) {
  const form = new FormData();
  form.append("file", file);

  await axios.post(
    `https://modularobjects.com/mailserver/api/bank/docs/${id}/upload`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } }
  );

  const docs = await loadDocumentsForTransaction(id);
  setDocumentsByTx((prev) => ({ ...prev, [id]: docs }));
}

async function loadDocumentsForTransaction(txId) {
  const res = await axios.get(
    `https://modularobjects.com/mailserver/api/bank/docs/${txId}`
  );
  return res.data?.data || [];
}

const [pdfUrl, setPdfUrl] = useState(null);

function openPdf(url) {
  setPdfUrl(url);
}
const loadCustomers = async () => {
  setLoadingCustomers(true);
  setCustomersError("");

  try {
    const params = {};
    if (transactionFilters.dateFrom) params.dateFrom = transactionFilters.dateFrom;
    if (transactionFilters.dateTo) params.dateTo = transactionFilters.dateTo;
    if (transactionFilters.search) params.search = transactionFilters.search;

    const res = await axios.get(
      "https://modularobjects.com/mailserver/api/bank/customers-summary",
      { params }
    );

    setCustomers(res.data?.data || []);
  } catch (err) {
    console.error("Greška pri učitavanju klijenata:", err);
    setCustomersError("Neuspešno učitavanje klijenata.");
  } finally {
    setLoadingCustomers(false);
  }
};

const loadProjectsSummary = async () => {
  setLoadingProjectsSummary(true);
  setProjectsSummaryError("");

  try {
    const params = {};
    if (transactionFilters.dateFrom) params.dateFrom = transactionFilters.dateFrom;
    if (transactionFilters.dateTo) params.dateTo = transactionFilters.dateTo;

    const res = await axios.get(
      "https://modularobjects.com/mailserver/api/bank/projects-summary",
      { params }
    );

    setProjectsSummary(res.data?.data || []);
  } catch (err) {
    console.error("Greška pri učitavanju projekta summary:", err);
    setProjectsSummaryError("Neuspešno učitavanje pregleda po projektima.");
  } finally {
    setLoadingProjectsSummary(false);
  }
};

  useEffect(() => {
  loadStatements();
  loadTransactions();
  loadSuppliers();
  loadCustomers();
  loadProjectsSummary();
}, []);

  const summary = useMemo(() => {
    const totalDebit = transactions
      .filter((t) => t.direction === "debit")
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const totalCredit = transactions
      .filter((t) => t.direction === "credit")
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const bankFees = transactions
      .filter((t) => t.booking_category === "bank_fee")
      .reduce((sum, t) => sum + Number(t.amount || 0), 0);

    const latestStatement = statements.length ? statements[0] : null;

    return {
      totalDebit,
      totalCredit,
      bankFees,
      latestBalance: latestStatement ? Number(latestStatement.closing_balance || 0) : 0,
      latestAccount: latestStatement?.account_number || "",
      statementsCount: statements.length,
      transactionsCount: transactions.length,
    };
  }, [statements, transactions]);

    const balanceChartData = useMemo(() => {
    return [...statements]
      .slice()
      .sort((a, b) => new Date(a.statement_date) - new Date(b.statement_date))
      .map((row) => ({
        date: fmtDate(row.statement_date),
        rawDate: row.statement_date,
        closing_balance: Number(row.closing_balance || 0),
        previous_balance: Number(row.previous_balance || 0),
      }));
  }, [statements]);

  const cashflowChartData = useMemo(() => {
    const grouped = {};

    for (const tx of transactions) {
      const date = tx.value_date || "unknown";
      if (!grouped[date]) {
        grouped[date] = {
          rawDate: date,
          date: fmtDate(date),
          debit: 0,
          credit: 0,
        };
      }

      const amount = Number(tx.amount || 0);

      if (tx.direction === "debit") {
        grouped[date].debit -= amount;
      } else if (tx.direction === "credit") {
        grouped[date].credit += amount;
      }
    }

    return Object.values(grouped).sort(
      (a, b) => new Date(a.rawDate) - new Date(b.rawDate)
    );
  }, [transactions]);

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-primary-light dark:text-primary-dark">
          🏦 Bank
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Pregled izvoda, transakcija i osnovnog cashflow stanja.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
          <div className="text-sm text-gray-500">Poslednje stanje</div>
          <div className="text-2xl font-bold mt-1">{fmtAmount(summary.latestBalance)} RSD</div>
          <div className="text-xs text-gray-500 mt-1">{summary.latestAccount || "—"}</div>
        </div>

        <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
          <div className="text-sm text-gray-500">Ukupni odlivi</div>
          <div className="text-2xl font-bold mt-1">{fmtAmount(summary.totalDebit)} RSD</div>
        </div>

        <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
          <div className="text-sm text-gray-500">Ukupni prilivi</div>
          <div className="text-2xl font-bold mt-1">{fmtAmount(summary.totalCredit)} RSD</div>
        </div>

        <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
          <div className="text-sm text-gray-500">Bankarske provizije</div>
          <div className="text-2xl font-bold mt-1">{fmtAmount(summary.bankFees)} RSD</div>
        </div>
      </div>

           <div className="grid gap-4 xl:grid-cols-2">
  <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
    <h2 className="text-lg font-semibold mb-4">Stanje računa kroz vreme</h2>
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={balanceChartData}
          margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateTick}
            minTickGap={20}
          />
          <YAxis
            width={75}
            tickFormatter={formatYAxisTick}
          />
          <Tooltip
            labelFormatter={formatTooltipLabel}
            formatter={(value) => [`${fmtAmount(value)} RSD`, "Krajnje stanje"]}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="closing_balance"
            name="Krajnje stanje"
            stroke="#d6c176"
            strokeWidth={3}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>

  <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
    <h2 className="text-lg font-semibold mb-4">Cashflow po danu</h2>
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={cashflowChartData}
          margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateTick}
            minTickGap={20}
          />
          <YAxis
            width={75}
            tickFormatter={formatYAxisTick}
          />
          <Tooltip
            labelFormatter={formatTooltipLabel}
            formatter={(value, name) => [
              `${fmtAmount(value)} RSD`,
              name === "debit" ? "Odlivi" : "Prilivi"
            ]}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="debit"
            name="Odlivi"
            stroke="#ef4444"
            strokeWidth={2}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="credit"
            name="Prilivi"
            stroke="#22c55e"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
</div>

      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveTab("statements")}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            activeTab === "statements"
              ? "bg-primary-light text-white"
              : "bg-gray-100 dark:bg-neutral-800"
          }`}
        >
          Izvodi
        </button>

        <button
          onClick={() => setActiveTab("transactions")}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            activeTab === "transactions"
              ? "bg-primary-light text-white"
              : "bg-gray-100 dark:bg-neutral-800"
          }`}
        >
          Transakcije
        </button>

        <button
  onClick={() => setActiveTab("suppliers")}
  className={`px-4 py-2 rounded-md text-sm font-medium ${
    activeTab === "suppliers"
      ? "bg-primary-light text-white"
      : "bg-gray-100 dark:bg-neutral-800"
  }`}
>
  Dobavljači
</button>

<button
  onClick={() => setActiveTab("customers")}
  className={`px-4 py-2 rounded-md text-sm font-medium ${
    activeTab === "customers"
      ? "bg-primary-light text-white"
      : "bg-gray-100 dark:bg-neutral-800"
  }`}
>
  Klijenti
</button>

<button
  onClick={() => setActiveTab("projects")}
  className={`px-4 py-2 rounded-md text-sm font-medium ${
    activeTab === "projects"
      ? "bg-primary-light text-white"
      : "bg-gray-100 dark:bg-neutral-800"
  }`}
>
  Projekti
</button>
      </div>

      {activeTab === "statements" && (
        <div className="space-y-4">
          <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Filteri izvoda</h2>

            <div className="grid gap-3 md:grid-cols-4">
              <input
                type="text"
                placeholder="Broj računa"
                value={statementFilters.accountNumber}
                onChange={(e) =>
                  setStatementFilters((prev) => ({ ...prev, accountNumber: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              />

              <input
                type="date"
                value={statementFilters.dateFrom}
                onChange={(e) =>
                  setStatementFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              />

              <input
                type="date"
                value={statementFilters.dateTo}
                onChange={(e) =>
                  setStatementFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              />

              <div className="flex gap-2">
                <button
                  onClick={loadStatements}
                  className="px-4 py-2 rounded-md bg-primary-light text-white"
                >
                  Primeni
                </button>
                <button
                  onClick={() => {
                    setStatementFilters({ accountNumber: "", dateFrom: "", dateTo: "" });
                    setTimeout(loadStatements, 0);
                  }}
                  className="px-4 py-2 rounded-md border"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Izvodi</h2>
              <div className="text-sm text-gray-500">Ukupno: {summary.statementsCount}</div>
            </div>

            {loadingStatements && <p>Učitavanje izvoda...</p>}
            {statementError && <p className="text-red-600">{statementError}</p>}

            {!loadingStatements && !statementError && (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead className="bg-gray-100 dark:bg-neutral-800">
                    <tr>
                      <th className="px-3 py-2 border text-left">Datum</th>
                      <th className="px-3 py-2 border text-left">Broj izvoda</th>
                      <th className="px-3 py-2 border text-left">Banka</th>
                      <th className="px-3 py-2 border text-left">Račun</th>
                      <th className="px-3 py-2 border text-right">Prethodno stanje</th>
                      <th className="px-3 py-2 border text-right">Dugovni promet</th>
                      <th className="px-3 py-2 border text-right">Potražni promet</th>
                      <th className="px-3 py-2 border text-right">Krajnje stanje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statements.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                        <td className="px-3 py-2 border">{fmtDate(row.statement_date)}</td>
                        <td className="px-3 py-2 border">{row.statement_no}</td>
                        <td className="px-3 py-2 border">{row.bank_name}</td>
                        <td className="px-3 py-2 border">{row.account_number}</td>
                        <td className="px-3 py-2 border text-right">{fmtAmount(row.previous_balance)}</td>
                        <td className="px-3 py-2 border text-right">{fmtAmount(row.total_debit)}</td>
                        <td className="px-3 py-2 border text-right">{fmtAmount(row.total_credit)}</td>
                        <td className="px-3 py-2 border text-right font-semibold">
                          {fmtAmount(row.closing_balance)}
                        </td>
                      </tr>
                    ))}
                    {!statements.length && (
                      <tr>
                        <td colSpan={8} className="px-3 py-6 border text-center text-gray-500">
                          Nema pronađenih izvoda.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "transactions" && (
        <div className="space-y-4">
          <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Filteri transakcija</h2>

            <div className="grid gap-3 md:grid-cols-6">
              <input
                type="text"
                placeholder="Broj računa"
                value={transactionFilters.accountNumber}
                onChange={(e) =>
                  setTransactionFilters((prev) => ({ ...prev, accountNumber: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              />

              <input
                type="date"
                value={transactionFilters.dateFrom}
                onChange={(e) =>
                  setTransactionFilters((prev) => ({ ...prev, dateFrom: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              />

              <input
                type="date"
                value={transactionFilters.dateTo}
                onChange={(e) =>
                  setTransactionFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              />

              <select
                value={transactionFilters.direction}
                onChange={(e) =>
                  setTransactionFilters((prev) => ({ ...prev, direction: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              >
                <option value="">Svi smerovi</option>
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>

              <select
                value={transactionFilters.bookingCategory}
                onChange={(e) =>
                  setTransactionFilters((prev) => ({ ...prev, bookingCategory: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              >
                <option value="">Sve kategorije</option>
                <option value="supplier_payment">Supplier payment</option>
                <option value="bank_fee">Bank fee</option>
                <option value="unknown">Unknown</option>
              </select>

              <input
                type="text"
                placeholder="Pretraga partner/opis/ref"
                value={transactionFilters.search}
                onChange={(e) =>
                  setTransactionFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                className="px-3 py-2 border rounded-md dark:bg-neutral-800 dark:text-white"
              />
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={loadTransactions}
                className="px-4 py-2 rounded-md bg-primary-light text-white"
              >
                Primeni
              </button>
              <button
                onClick={() => {
                  setTransactionFilters({
                    accountNumber: "",
                    dateFrom: "",
                    dateTo: "",
                    direction: "",
                    bookingCategory: "",
                    search: "",
                  });
                  setTimeout(loadTransactions, 0);
                }}
                className="px-4 py-2 rounded-md border"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Transakcije</h2>
              <div className="text-sm text-gray-500">Ukupno: {summary.transactionsCount}</div>
            </div>

            {loadingTransactions && <p>Učitavanje transakcija...</p>}
            {transactionError && <p className="text-red-600">{transactionError}</p>}

            {!loadingTransactions && !transactionError && (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse">
                  <thead className="bg-gray-100 dark:bg-neutral-800">
                    <tr>
  <th className="px-3 py-2 border text-left">Datum valute</th>
  <th className="px-3 py-2 border text-left">Smer</th>
  <th className="px-3 py-2 border text-right">Iznos</th>
  <th className="px-3 py-2 border text-left">Partner</th>
  <th className="px-3 py-2 border text-left">Opis</th>
  <th className="px-3 py-2 border text-left">Bank kategorija</th>
  <th className="px-3 py-2 border text-left">Poziv na broj</th>
  <th className="px-3 py-2 border text-left">Referenca</th>
  <th className="px-3 py-2 border text-left">Račun</th>
  <th className="px-3 py-2 border text-left">Kategorija</th>
  <th className="px-3 py-2 border text-left">Podkategorija</th>
  <th className="px-3 py-2 border text-left">Projekat</th>
  <th className="px-3 py-2 border text-left">Akcija</th>
  <th className="px-3 py-2 border text-left">Dokument</th>
</tr>
                  </thead>
                  <tbody>
                    {transactions.map((row) => (
  <React.Fragment key={row.id}>
    <tr className="hover:bg-gray-50 dark:hover:bg-neutral-800">
      <td className="px-3 py-2 border">{fmtDate(row.value_date)}</td>
      <td className="px-3 py-2 border">
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${
            row.direction === "debit"
              ? "bg-red-100 text-red-700"
              : "bg-green-100 text-green-700"
          }`}
        >
          {row.direction}
        </span>
      </td>
      <td className="px-3 py-2 border text-right font-medium">
        {fmtAmount(row.amount)} {row.currency || "RSD"}
      </td>
      <td className="px-3 py-2 border">{row.counterparty_name || "—"}</td>
      <td className="px-3 py-2 border">{row.description || "—"}</td>
      <td className="px-3 py-2 border">{row.booking_category || "—"}</td>
      <td className="px-3 py-2 border">{row.call_number_user || "—"}</td>
      <td className="px-3 py-2 border">{row.reference_no || "—"}</td>
      <td className="px-3 py-2 border">{row.account_number || "—"}</td>
      <td className="px-3 py-2 border">{row.category || "—"}</td>
      <td className="px-3 py-2 border">{row.subcategory || "—"}</td>
      <td className="px-3 py-2 border">{row.project_id || "—"}</td>
      <td className="px-3 py-2 border">
        <button
          onClick={() => startClassificationEdit(row)}
          className="px-3 py-1 rounded bg-blue-600 text-white text-sm"
        >
          Classify
        </button>
      </td>
      <td className="px-3 py-2 border text-center">
  <div className="flex items-center gap-2">
    <label className="cursor-pointer">
      ⬆️
      <input
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) uploadDoc(row.id, file);
        }}
      />
    </label>

    <button
      onClick={async () => {
        const docs = await loadDocumentsForTransaction(row.id);
        setDocumentsByTx((prev) => ({ ...prev, [row.id]: docs }));
        setDocumentsModalTx(row.id);
        setDocumentsModalItems(docs);
      }}
      className="px-2 py-1 rounded border"
      title="Pogledaj dokumenta"
    >
      📄 {documentsByTx[row.id]?.length || 0}
    </button>
  </div>
</td>
    </tr>

   

    {editingTxId === row.id && (
      <tr>
        <td colSpan={14} className="px-3 py-4 border bg-gray-50 dark:bg-neutral-800">
          <div className="grid gap-3 md:grid-cols-4">
            <input
              type="number"
              placeholder="Project ID"
              value={classificationForm.projectId}
              onChange={(e) =>
                setClassificationForm((prev) => ({
                  ...prev,
                  projectId: e.target.value,
                }))
              }
              className="px-3 py-2 border rounded-md dark:bg-neutral-900 dark:text-white"
            />

            <select
              value={classificationForm.category}
              onChange={(e) =>
                setClassificationForm((prev) => ({
                  ...prev,
                  category: e.target.value,
                }))
              }
              className="px-3 py-2 border rounded-md dark:bg-neutral-900 dark:text-white"
            >
              <option value="">Izaberi kategoriju</option>
              <option value="revenue">Revenue</option>
              <option value="advance_payment">Advance payment</option>
              <option value="supplier_payment">Supplier payment</option>
              <option value="material_cost">Material cost</option>
              <option value="project_cost">Project cost</option>
              <option value="salary">Salary</option>
              <option value="tax">Tax</option>
              <option value="bank_fee">Bank fee</option>
              <option value="transfer">Transfer</option>
              <option value="loan">Loan</option>
              <option value="refund">Refund</option>
              <option value="other">Other</option>
            </select>

            <input
              type="text"
              placeholder="Subcategory"
              value={classificationForm.subcategory}
              onChange={(e) =>
                setClassificationForm((prev) => ({
                  ...prev,
                  subcategory: e.target.value,
                }))
              }
              className="px-3 py-2 border rounded-md dark:bg-neutral-900 dark:text-white"
            />

            <input
              type="text"
              placeholder="Note"
              value={classificationForm.note}
              onChange={(e) =>
                setClassificationForm((prev) => ({
                  ...prev,
                  note: e.target.value,
                }))
              }
              className="px-3 py-2 border rounded-md dark:bg-neutral-900 dark:text-white"
            />
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={saveClassification}
              className="px-4 py-2 rounded-md bg-green-600 text-white"
            >
              Save
            </button>
            <button
              onClick={cancelClassificationEdit}
              className="px-4 py-2 rounded-md border"
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    )}
  </React.Fragment>
))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

 {activeTab === "suppliers" && (
  <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold">Dobavljači</h2>
      <button onClick={loadSuppliers} className="px-4 py-2 rounded-md bg-primary-light text-white">
        Refresh
      </button>
    </div>

    {loadingSuppliers && <p>Učitavanje dobavljača...</p>}
    {suppliersError && <p className="text-red-600">{suppliersError}</p>}

    {!loadingSuppliers && !suppliersError && (
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100 dark:bg-neutral-800">
            <tr>
              <th className="px-3 py-2 border text-left">Dobavljač</th>
              <th className="px-3 py-2 border text-right">Broj transakcija</th>
              <th className="px-3 py-2 border text-right">Ukupno plaćeno</th>
              <th className="px-3 py-2 border text-left">Poslednje plaćanje</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((row, idx) => (
              <tr key={`${row.counterparty_name}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                <td className="px-3 py-2 border">{row.counterparty_name || "—"}</td>
                <td className="px-3 py-2 border text-right">{row.transactions_count}</td>
                <td className="px-3 py-2 border text-right">{fmtAmount(row.total_paid)} RSD</td>
                <td className="px-3 py-2 border">{fmtDate(row.last_payment_date)}</td>
              </tr>
            ))}
            {!suppliers.length && (
              <tr>
                <td colSpan={4} className="px-3 py-6 border text-center text-gray-500">
                  Nema podataka za dobavljače.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

{activeTab === "customers" && (
  <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold">Klijenti</h2>
      <button onClick={loadCustomers} className="px-4 py-2 rounded-md bg-primary-light text-white">
        Refresh
      </button>
    </div>

    {loadingCustomers && <p>Učitavanje klijenata...</p>}
    {customersError && <p className="text-red-600">{customersError}</p>}

    {!loadingCustomers && !customersError && (
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100 dark:bg-neutral-800">
            <tr>
              <th className="px-3 py-2 border text-left">Klijent</th>
              <th className="px-3 py-2 border text-right">Broj uplata</th>
              <th className="px-3 py-2 border text-right">Ukupno naplaćeno</th>
              <th className="px-3 py-2 border text-left">Poslednja uplata</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((row, idx) => (
              <tr key={`${row.counterparty_name}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                <td className="px-3 py-2 border">{row.counterparty_name || "—"}</td>
                <td className="px-3 py-2 border text-right">{row.transactions_count}</td>
                <td className="px-3 py-2 border text-right">{fmtAmount(row.total_received)} RSD</td>
                <td className="px-3 py-2 border">{fmtDate(row.last_payment_date)}</td>
              </tr>
            ))}
            {!customers.length && (
              <tr>
                <td colSpan={4} className="px-3 py-6 border text-center text-gray-500">
                  Nema podataka za klijente.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

{activeTab === "projects" && (
  <div className="border rounded-lg p-4 bg-white dark:bg-neutral-900 shadow-sm">
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold">Projekti</h2>
      <button onClick={loadProjectsSummary} className="px-4 py-2 rounded-md bg-primary-light text-white">
        Refresh
      </button>
    </div>

    {loadingProjectsSummary && <p>Učitavanje projekata...</p>}
    {projectsSummaryError && <p className="text-red-600">{projectsSummaryError}</p>}

    {!loadingProjectsSummary && !projectsSummaryError && (
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100 dark:bg-neutral-800">
            <tr>
              <th className="px-3 py-2 border text-left">Project ID</th>
              <th className="px-3 py-2 border text-right">Broj transakcija</th>
              <th className="px-3 py-2 border text-right">Prihodi</th>
              <th className="px-3 py-2 border text-right">Rashodi</th>
              <th className="px-3 py-2 border text-right">Neto</th>
            </tr>
          </thead>
          <tbody>
            {projectsSummary.map((row, idx) => (
              <tr key={`${row.project_id}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                <td className="px-3 py-2 border">{row.project_id || "—"}</td>
                <td className="px-3 py-2 border text-right">{row.transactions_count}</td>
                <td className="px-3 py-2 border text-right">{fmtAmount(row.total_inflow)} RSD</td>
                <td className="px-3 py-2 border text-right">{fmtAmount(row.total_outflow)} RSD</td>
                <td className="px-3 py-2 border text-right font-semibold">{fmtAmount(row.net_amount)} RSD</td>
              </tr>
            ))}
            {!projectsSummary.length && (
              <tr>
                <td colSpan={5} className="px-3 py-6 border text-center text-gray-500">
                  Nema podataka za projekte. Potrebno je da klasifikuješ transakcije po project_id.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )}
  </div>
)}

{documentsModalTx && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
    <div className="bg-white dark:bg-neutral-900 w-[90vw] h-[90vh] rounded-lg shadow-lg p-4 relative overflow-hidden">
      <button
        onClick={() => {
          setDocumentsModalTx(null);
          setDocumentsModalItems([]);
        }}
        className="absolute top-3 right-3 px-3 py-1 rounded border"
      >
        Close
      </button>

      <div className="grid grid-cols-12 gap-4 h-full pt-10">
        <div className="col-span-3 border-r pr-3 overflow-y-auto">
          <h3 className="font-semibold mb-3">Dokumenti</h3>
          <div className="space-y-2">
            {documentsModalItems.map((doc) => (
              <button
                key={doc.id}
                onClick={() =>
                  setPdfUrl(`https://modularobjects.com/mailserver${doc.file_path}`)
                }
                className="block w-full text-left px-3 py-2 rounded border hover:bg-gray-50 dark:hover:bg-neutral-800"
              >
                {doc.original_filename}
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-9 h-full">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              width="100%"
              height="100%"
              title="PDF"
              className="rounded"
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              Izaberi dokument sa leve strane.
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}

    </div>
  );
}